import createClient, { type Client, type Middleware } from 'openapi-fetch'

import { env } from '@app/config/env'
import { fakeAuthRoleHeader, readFakeAuthRole, type FakeAuthRole } from '@app/auth/local-principal'
import type { components, paths } from './generated/admin-v1'

export type AdminApiClient = Client<paths>
export type ErrorEnvelope = components['schemas']['ErrorEnvelope']
export type CursorPageMeta = components['schemas']['CursorPageMeta']
export type CursorPage<T> = {
	items: T[]
	page: CursorPageMeta
}

export type ApiSuccess<T> = {
	data: T
	etag: string | null
	requestId: string | null
	response: Response
}

export class AdminApiError extends Error {
	readonly status: number
	readonly code: string
	readonly requestId: string | null
	readonly details: unknown

	constructor(options: {
		status: number
		code: string
		message: string
		requestId?: string | null
		details?: unknown
	}) {
		super(options.message)
		this.name = 'AdminApiError'
		this.status = options.status
		this.code = options.code
		this.requestId = options.requestId ?? null
		this.details = options.details
	}
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null

export const toAdminApiError = (error: unknown, response: Response) => {
	const envelope = isRecord(error) ? error : {}
	const code = typeof envelope.code === 'string' ? envelope.code : 'UNEXPECTED_RESPONSE'
	const message =
		typeof envelope.message === 'string'
			? envelope.message
			: `Запрос к административному API завершился со статусом ${response.status}`
	const requestId =
		typeof envelope.request_id === 'string'
			? envelope.request_id
			: response.headers.get('x-request-id')
	return new AdminApiError({
		status: response.status,
		code,
		message,
		requestId,
		details: envelope.details,
	})
}

export const unwrapApiResponse = <T>(result: {
	data?: T
	error?: unknown
	response: Response
}): ApiSuccess<T> => {
	if (result.error !== undefined) {
		throw toAdminApiError(result.error, result.response)
	}
	if (result.data === undefined) {
		throw new AdminApiError({
			status: result.response.status,
			code: 'EMPTY_RESPONSE',
			message: 'Административный API вернул ответ без данных',
			requestId: result.response.headers.get('x-request-id'),
		})
	}
	return {
		data: result.data,
		etag: result.response.headers.get('etag'),
		requestId: result.response.headers.get('x-request-id'),
		response: result.response,
	}
}

export const createIdempotencyKey = () => crypto.randomUUID()

export const createCommandHeaders = (options: {
	idempotencyKey?: string
	etag?: string
} = {}) => {
	const headers: Record<string, string> = {}
	if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey
	if (options.etag) headers['If-Match'] = options.etag
	return headers
}

export const getNextCursor = (page: CursorPageMeta | null | undefined) =>
	page?.next_cursor ?? null

export const applyAdminRequestHeaders = async (
	request: Request,
	getAccessToken: () => Promise<string | null>,
	createRequestId: () => string = () => crypto.randomUUID(),
	fakeAuthRole: FakeAuthRole | null = null,
) => {
	const token = await getAccessToken()
	if (token) request.headers.set('Authorization', `Bearer ${token}`)
	if (!token && fakeAuthRole) request.headers.set(fakeAuthRoleHeader, fakeAuthRole)
	request.headers.set('Accept', 'application/json')
	request.headers.set('X-Request-Id', createRequestId())
	return request
}

export const createAdminApiClient = (getAccessToken: () => Promise<string | null>) => {
	const client = createClient<paths>({ baseUrl: env.adminApiUrl })
	const middleware: Middleware = {
		onRequest: ({ request }) => applyAdminRequestHeaders(request, getAccessToken, undefined, env.authEnabled ? null : readFakeAuthRole()),
	}
	client.use(middleware)
	return client
}
